import CsvDropzone from 'components/atoms/CsvDropzone'
import { CloudUploadIcon } from '@heroicons/react/solid'
import { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, Transition } from '@headlessui/react'
import apiPost from 'lib/network/apiPost'
import notify from 'components/atoms/Notify'
import Selection from 'components/molecules/Selection'
import { isArray, isNumber } from 'lodash'
import apiGet from 'lib/network/apiGet'

const ImportUsers = ({ close, refetch, companyNames }) => {
  const { t } = useTranslation()
  const [updateResult, setUpdateResult] = useState(null)
  const [importMode, setImportMode] = useState(null)
  const [file, setFile] = useState(null)
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [selectedBranch, setSelectedBranch] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [errorMessages, setErrorMessages] = useState({})
  const [branches, setBranches] = useState([])

  useEffect(() => {
    setBranches([])
    setSelectedBranch(null)

    if (selectedCompany) {
      fetchBranches(selectedCompany)
    }
  }, [selectedCompany])

  const fetchBranches = async companyId => {
    try {
      const response = await apiGet(`company/${companyId}/branches`)
      if (response?.data) {
        const branchList = response.data.map(branch => ({
          id: branch._id,
          name: branch.name,
        }))
        setBranches(branchList)
      } else {
        setBranches([])
      }
    } catch (error) {
      notify.error('Failed to fetch branches')
      setBranches([])
    }
  }

  function onDrop(file) {
    if (!file || !file.type === 'application/zip') return
    setFile(file)
  }

  const resetImportType = () => {
    setImportMode(null)
    setFile(null)
    setErrorMessages({})
  }

  const dismissImportUpdateResult = () => {
    setUpdateResult(null)
    setFile(null)
    setErrorMessages({})
    close()
    notify.success(t('cars.buttons.importCars'), t('cars.import.importSuccess'))
    refetch()
  }

  const saveImportedCsv = async () => {
    if (!file) {
      return null
    }

    setUploading(true)

    const formData = new FormData()
    formData.append('carExcel', file)

    let endpoint = 'import-cars'
    if (importMode === 'existing') {
      endpoint = 'cars/import-update'
    } else {
      formData.append('companyId', selectedCompany)
      formData.append('branchId', selectedBranch)
    }

    const response = await apiPost(endpoint, formData)
    if (response.success) {
      if (importMode === 'existing') {
        const data = response.data || {}
        setUpdateResult({
          updatedCount: isNumber(data.updatedCount) ? data.updatedCount : 0,
          errors: isArray(data.errors) ? data.errors : [],
        })
        setUploading(false)
        return
      }
      close()
      notify.success(t('cars.buttons.importCars'), t('cars.import.importSuccess'))
      refetch()
    } else {
      try {
        const errors = JSON.parse(response.err)
        setErrorMessages(errors)
      } catch (error) {}
    }
    setUploading(false)
  }

  const uploadDisabled = uploading || !file || (importMode === 'new' && (!selectedCompany || !selectedBranch))

  return (
    <>
      <Transition.Root show={!!updateResult} as={Fragment}>
        <Dialog as="div" className="fixed z-[100] inset-0 overflow-y-auto" onClose={dismissImportUpdateResult}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>
          <div className="flex min-h-screen items-center justify-center p-4 sm:p-6">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <div className="relative w-full max-w-[min(92vw,72rem)] max-h-[85vh] flex flex-col rounded-lg bg-white text-left shadow-xl transform transition-all">
                <div className="border-b border-gray-100 py-4 px-6 flex-shrink-0">
                  <Dialog.Title className="text-lg font-bold text-gray-900">{t('cars.import.updateResultTitle')}</Dialog.Title>
                </div>
                <div className="px-6 py-4 overflow-y-auto flex-1 min-h-0">
                  {updateResult && (
                    <>
                      <p className="text-sm text-gray-800 mb-4">{t('cars.import.updatedCount', { count: updateResult.updatedCount })}</p>
                      {updateResult.errors.length > 0 && (
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">{t('cars.import.errorsHeading')}</h4>
                          <div className="overflow-x-auto border border-gray-200 rounded-md">
                            <table className="min-w-full divide-y divide-gray-200 text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left font-medium text-gray-700">{t('cars.import.errorRow')}</th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-700">{t('cars.import.errorCarId')}</th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-700">{t('cars.import.errorCode')}</th>
                                  <th className="px-3 py-2 text-left font-medium text-gray-700">{t('cars.import.errorMessage')}</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-200 bg-white">
                                {updateResult.errors.map((err, idx) => (
                                  <tr key={`${err.row}-${err.carUniqueId}-${idx}`} className="align-top">
                                    <td className="px-3 py-2 text-gray-800 whitespace-nowrap">{err.row}</td>
                                    <td className="px-3 py-2 text-gray-600 font-mono text-xs break-all max-w-[min(24vw,240px)]">{err.carUniqueId}</td>
                                    <td className="px-3 py-2 text-gray-800 whitespace-nowrap">{err.code}</td>
                                    <td className="px-3 py-2 text-red-700 text-xs">{err.message}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div className="border-t border-gray-100 px-6 py-4 flex justify-end flex-shrink-0">
                  <button
                    type="button"
                    onClick={dismissImportUpdateResult}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-dark-600 hover:bg-dark-500 focus:outline-none transition duration-150"
                  >
                    {t('common.close')}
                  </button>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition.Root>

      {importMode === null ? (
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600">{t('cars.import.chooseType')}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => setImportMode('new')}
              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-800 bg-white hover:border-dark-500 hover:bg-gray-50 transition-colors"
            >
              {t('cars.import.newCars')}
            </button>
            <button
              type="button"
              onClick={() => setImportMode('existing')}
              className="flex-1 px-4 py-3 border-2 border-gray-200 rounded-lg text-sm font-medium text-gray-800 bg-white hover:border-dark-500 hover:bg-gray-50 transition-colors"
            >
              {t('cars.import.updateExisting')}
            </button>
          </div>
          <div className="flex justify-end mt-2">
            <button
              type="button"
              onClick={() => close()}
              className="inline-flex items-center px-4 py-2 border shadow text-sm leading-5 font-medium rounded-md text-dark-500 bg-white transition duration-150 ease-in-out"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-3">
            <button type="button" onClick={resetImportType} className="text-sm text-blue-600 hover:text-blue-800 font-medium">
              ← {t('cars.import.changeType')}
            </button>
          </div>

          {importMode === 'new' && (
            <div className="flex justify-between gap-2">
              <Selection
                value={selectedCompany}
                onChange={e => {
                  setSelectedCompany(e.id)
                }}
                className="w-full mb-3"
                list={companyNames}
                title={t('cars.filters.company')}
              />
              <Selection
                value={selectedBranch}
                onChange={e => {
                  setSelectedBranch(e.id)
                }}
                className="w-full mb-3"
                list={branches}
                title={t('cars.filters.branch')}
              />
            </div>
          )}

          <CsvDropzone onDrop={onDrop} setFile={setFile} setErrorMessages={setErrorMessages} errorMessages={errorMessages} showSampleDownload={importMode !== 'existing'} />
          {isArray(errorMessages) &&
            errorMessages?.map((error, index) => {
              return (
                <p className="text-xs flex text-red-600 gap-2 mt-1 overflow-hidden" key={index}>
                  <b>{error?.sheet}:</b>
                  {error?.error}
                </p>
              )
            })}
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => close()}
              className="inline-flex items-center px-4 py-2 border shadow mr-4 text-sm leading-5 font-medium rounded-md text-dark-500 bg-white transition duration-150 ease-in-out"
            >
              {t('common.close')}
            </button>
            <button
              disabled={uploadDisabled}
              onClick={() => saveImportedCsv()}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-dark-600 hover:bg-dark-500 focus:outline-none focus:shadow-outline-dark focus:border-dark-700 active:bg-dark-700 transition duration-150 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {!uploading ? (
                <CloudUploadIcon className="h-5 w-5 mr-3" aria-hidden="true" />
              ) : (
                <svg className="animate-spin h-5 w-5 mr-3 text-gray-500" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="white"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              )}
              {t('cars.buttons.import')}
            </button>
          </div>
        </>
      )}
    </>
  )
}

export default ImportUsers
